import { persistedExchange } from '@urql/exchange-persisted';
import {
  cacheExchange,
  Client,
  type Exchange,
  fetchExchange,
  getOperationName,
  makeOperation,
  type SSRExchange,
  ssrExchange,
} from '@urql/vue';
// @ts-expect-error provided via vite alias
import { fetch, fetchOptions } from 'apolloFetchOptions';
import { map, pipe } from 'wonka';

import type { FischerPlugin } from '@/lib/fischerPlugin';
import type { SitecoreDeploymentEnv } from '@/sitecoreState';
import sha256 from '@/utils/sha256Digest';

export type FischerBox = { plugin: FischerPlugin | null };

export const createTokenInjectionExchange =
  (fischerBox: FischerBox): Exchange =>
  ({ forward }) =>
  (ops$) =>
    forward(
      pipe(
        ops$,
        map((operation) => {
          const fischer = fischerBox.plugin;
          if (!fischer) return operation;
          const vars = operation.variables as Record<string, unknown>;
          if (!vars) return operation;
          const hasAccessToken = 'accessToken' in vars;
          const hasIdToken = 'idToken' in vars;
          if (!hasAccessToken && !hasIdToken) return operation;
          const newVars = {
            ...vars,
            ...(hasAccessToken && { accessToken: fischer.user.accessToken() || vars.accessToken }),
            ...(hasIdToken && { idToken: fischer.user.idToken() || vars.idToken }),
          };
          return {
            ...makeOperation(operation.kind, operation, operation.context),
            variables: newVars,
          };
        }),
      ),
    );

/**
 * Operations whose *variables* are credentials in their own right, so they must never be
 * serialised into a URL.
 *
 * - getFixperienceOfflineRegistration: activationKey + validationKey together authorise
 *   reading a real person's home address.
 * - getIdsSession: sid is a single-use IDS session handle that establishes a login.
 *
 * persistedExchange forces GET for every query it hashes, and urql then serialises the
 * variables into the query string - where ingress/AFD access logs, the gateway's own
 * request-URL logging and Sentry fetch breadcrumbs all record them.
 */
export const forcePostOperations = ['getFixperienceOfflineRegistration', 'getIdsSession'];

/**
 * Puts operations back on POST by clearing the `preferGetMethod` context flag that
 * persistedExchange sets. Applies to {@link forcePostOperations}, or to every operation
 * when `forceAll` is set - which is the default in the showroom, where a readable request
 * body beats a hashed query string in the network panel.
 *
 * Position matters: persistedExchange assigns `context.preferGetMethod` on its way down the
 * chain, and fetchExchange reads it as the very last step before building the request
 * (makeFetchURL/serializeBody, where the method is literally `body ? 'POST' : 'GET'`). This
 * exchange therefore has to sit strictly *after* persistedExchange and *before*
 * fetchExchange - see createGraphQLClient below. Placing it earlier means persistedExchange
 * simply overwrites the flag again, silently reintroducing the leak. This mirrors the
 * ordering constraint the Apollo implementation had before the urql migration.
 *
 * Note there is no way to do this via `fetchOptions.method`: urql spreads those options
 * first and then overwrites `method` from the presence of a body, so `preferGetMethod` is
 * the only lever.
 */
export const createForcePostExchange =
  (opts: { operationNames?: string[]; forceAll?: boolean } = {}): Exchange =>
  ({ forward }) =>
  (ops$) => {
    const { operationNames = forcePostOperations, forceAll = IS_SHOWROOM } = opts;
    return forward(
      pipe(
        ops$,
        map((operation) => {
          if (!forceAll) {
            const name = getOperationName(operation.query);
            if (!name || !operationNames.includes(name)) return operation;
          }
          return makeOperation(operation.kind, operation, {
            ...operation.context,
            preferGetMethod: false,
          });
        }),
      ),
    );
  };

/**
 * The one place the ordering above is expressed, so that a test can pin it. Both
 * createGraphQLClient() and the unit test compose the chain through here - a hand-rebuilt
 * chain in a test could silently drift from the real one, which would leave exactly the
 * regression this guards against undetected.
 *
 * `persisted` is null when APQ is off (showroom / dev VMs without crypto.subtle); nothing
 * sets preferGetMethod then, so plain POST applies and the force-post exchange is a no-op.
 * It is still wired in unconditionally so that turning APQ on - in the showroom or anywhere
 * else - cannot quietly take the POST guarantees with it.
 */
export const composePersistedQueryChain = (
  persisted: Exchange | null,
  tokenInjection: Exchange,
  terminating: Exchange,
  forcePost: Exchange = createForcePostExchange(),
): Exchange[] => [...(persisted ? [persisted] : []), tokenInjection, forcePost, terminating];

const getEndpoint = (color?: SitecoreDeploymentEnv): string =>
  (IS_SHOWROOM && import.meta.env.VITE_APOLLO_CONFIG) ||
  `${import.meta.env.VITE_API_URL}/graphql-rebrush/${color || ''}`;

/**
 * IMPORTANT for any query that is prefetched in `onServerPrefetch`: leave it on the default
 * `cache-first` request policy. ssrExchange filters `requestPolicy === 'network-only'` out of
 * its restore path *before* it ever looks the operation key up, so a network-only query throws
 * the serialised SSR result away and refetches on hydration - the component renders empty over
 * the SSR markup until the response lands, which is visible as a flash.
 *
 * `network-only` is only safe (as the `no-cache` replacement) on queries that never take part in
 * SSR: client-only/gated queries, imperative `client.query(...)` calls, and explicit
 * `executeQuery({ requestPolicy: 'network-only' })` refreshes after the first paint.
 *
 * SECOND, EASILY MISSED HALF: a restored result is delivered *synchronously while the component
 * sets up*, whereas a fetched one always arrives after mount. Apollo only ever delivered
 * asynchronously, so components written against it commonly copy query data into their own
 * render state from a lazy `watch(data, ...)`. Such a watcher never fires for a restored result -
 * the ref is already populated before the watcher is registered - and the component renders
 * empty. Any component whose query is SSR-prefetched must therefore consume `data` through a
 * `computed`, or through a `watch(..., { immediate: true })`.
 *
 * THIRD HALF, and the reason those watchers also carry `flush: 'sync'`: Vue does not run ordinary
 * watchers during SSR at all. `doWatch` returns a no-op for a lazy watcher, and an `immediate`
 * one is created with `once: true` - so it fires exactly once during setup, before
 * onServerPrefetch has resolved anything, and is then disposed. Only a `flush: 'sync'` watcher
 * still fires when the prefetched result lands, which is what gets the data into the server HTML.
 * `{ immediate: true, flush: 'sync' }` therefore covers both paths: immediate for the client
 * (restored during setup), sync for the server (arrives after setup, before render).
 *
 * State that is *derived* - a `computed` read straight from `data` - needs none of this and is
 * the better shape where the component allows it. ProductDetailPageFi is the example: its list
 * renders from a computed and only its tracking hangs off a watcher, which is why it kept working
 * server-side when the pushed-state components shipped as spinners. The Apollo originals used
 * vue-apollo's `onResult()` callback, which - unlike a Vue watcher - does fire during SSR; that
 * is the detail the migration to `watch()` silently dropped.
 *
 * Beware that an immediate callback runs inside `setup()`: everything it touches has to be
 * declared above it, or it throws a TDZ ReferenceError (`flush: 'post'` does NOT defer that
 * first invocation). Two ways out, both in use:
 *   - move the declaration above the watcher (ProductListViewFi does this with setLegend);
 *   - keep the render-state writes in the immediate callback and guard the rest behind an
 *     `isMounted` flag, replaying it from onMounted (SearchFactFinderResultsFi does this for the
 *     paging/backoff/price effects, which are post-paint concerns anyway).
 * Reaching for `network-only` instead is not a fix - it just brings the flash back.
 */
export const createSsrExchange = (): SSRExchange => ssrExchange({ isClient: !IS_SSR });

export const createGraphQLClient = (opts: {
  color?: SitecoreDeploymentEnv;
  referrer?: string | false;
  fischerBox?: FischerBox;
  ssr: SSRExchange;
  /** Debug-only tap that records each operation's variables; see createSsrOperationRecorder. */
  operationRecorder?: Exchange;
}): Client => {
  const { color, referrer, fischerBox = { plugin: null }, ssr, operationRecorder } = opts;

  // APQ disabled for showroom (easier debugging) and http dev VMs lacking crypto.subtle
  const apqEnabled =
    !IS_SHOWROOM && (IS_SSR || (typeof window !== 'undefined' && !!window.crypto?.subtle));

  const exchanges: Exchange[] = [
    // first in the chain: records operations as issued, before the cache can answer them
    ...(operationRecorder ? [operationRecorder] : []),
    cacheExchange,
    ssr,
    ...composePersistedQueryChain(
      apqEnabled
        ? persistedExchange({
            preferGetForPersistedQueries: true,
            // generateHash receives the stringified query string as the first argument.
            // urql's stringifyDocument normalizes via graphql.print() + sanitizeDocument
            // (strips comments, normalizes newlines), whereas Apollo hashes raw print(query).
            // These are likely identical for .gql files with no comments, but may differ
            // if queries contain comments or non-standard whitespace. Flag for backend verification.
            generateHash: (query) => sha256(query),
          })
        : null,
      createTokenInjectionExchange(fischerBox),
      fetchExchange,
    ),
  ];

  return new Client({
    url: getEndpoint(color),
    exchanges,
    fetch: fetch as typeof globalThis.fetch,
    fetchOptions: {
      ...fetchOptions,
      headers: {
        'content-type': 'application/json',
        ...(IS_SSR && referrer ? { referer: referrer } : {}),
      },
    },
    suspense: false,
  });
};
